// src/products/analysis/pages/PortfolioPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Trash2, RefreshCw, Loader2, AlertCircle, ExternalLink, LineChart, Newspaper } from 'lucide-react';
import { useGlobalStore } from '../../../state/globalStore';
import { getUserPortfolio, removeStockFromPortfolio, updatePortfolioStock } from '../services/portfolioService';
import { getStockData } from '../services/finnhubService';

const PortfolioPage = () => {
  const { user } = useGlobalStore();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    loadPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadPortfolio = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use default user ID for localStorage since it's shared
      const userId = user?.id || 'default_user';
      const data = await getUserPortfolio(userId);
      setPortfolio(data);
    } catch (err) {
      setError('Failed to load portfolio. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStock = async (stockId, symbol) => {
    if (!window.confirm(`Remove ${symbol} from your portfolio?`)) return;

    try {
      await removeStockFromPortfolio(stockId);
      setPortfolio(portfolio.filter(stock => stock.id !== stockId));
    } catch (err) {
      setError('Failed to remove stock. Please try again.');
      console.error(err);
    }
  };

  const handleRefreshStock = async (stock) => {
    setUpdating({ ...updating, [stock.id]: true });
    setError(null);

    try {
      const freshData = await getStockData(stock.symbol);
      await updatePortfolioStock(stock.id, freshData);
      
      // Update local state
      setPortfolio(portfolio.map(item => 
        item.id === stock.id 
          ? {
              ...item,
              current_price: freshData._currentPrice,
              current_price_formatted: freshData.currentPrice,
              market_cap: freshData._marketCap,
              market_cap_formatted: freshData.marketCap,
              enterprise_value: freshData._enterpriseValue,
              enterprise_value_formatted: freshData.enterpriseValue,
              shares_outstanding: freshData._sharesOutstanding,
              shares_outstanding_formatted: freshData.sharesOutstanding,
              updated_at: new Date().toISOString()
            }
          : item
      ));
    } catch (err) {
      setError(`Failed to refresh ${stock.symbol}. Please try again.`);
      console.error(err);
    } finally {
      setUpdating({ ...updating, [stock.id]: false });
    }
  };

  const handleRefreshAll = async () => {
    setUpdating({ all: true });
    setError(null);

    try {
      const refreshPromises = portfolio.map(async (stock) => {
        try {
          const freshData = await getStockData(stock.symbol);
          await updatePortfolioStock(stock.id, freshData);
          return {
            ...stock,
            current_price: freshData._currentPrice,
            current_price_formatted: freshData.currentPrice,
            market_cap: freshData._marketCap,
            market_cap_formatted: freshData.marketCap,
            enterprise_value: freshData._enterpriseValue,
            enterprise_value_formatted: freshData.enterpriseValue,
            shares_outstanding: freshData._sharesOutstanding,
            shares_outstanding_formatted: freshData.sharesOutstanding,
            updated_at: new Date().toISOString()
          };
        } catch (err) {
          console.error(`Failed to refresh ${stock.symbol}:`, err);
          return stock; // Keep old data if refresh fails
        }
      });

      const updatedPortfolio = await Promise.all(refreshPromises);
      setPortfolio(updatedPortfolio);
    } catch (err) {
      setError('Failed to refresh some stocks. Please try again.');
      console.error(err);
    } finally {
      setUpdating({ all: false });
    }
  };

  // Portfolio works with localStorage, so no need to check for user auth

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">View Portfolio</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your saved stocks and track financial metrics
          </p>
        </div>
        {portfolio.length > 0 && (
          <button
            onClick={handleRefreshAll}
            disabled={updating.all}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {updating.all ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh All
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-gray-600">Loading portfolio...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && portfolio.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No stocks in portfolio</h3>
          <p className="text-gray-500 mb-4">
            Start by searching for companies and saving them to your portfolio.
          </p>
          <a
            href="/market-data/find-company"
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Find Company
          </a>
        </div>
      )}

      {/* Portfolio Grid */}
      {!loading && portfolio.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolio.map((stock) => (
            <div
              key={stock.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {stock.logo && (
                    <img
                      src={stock.logo}
                      alt={stock.name}
                      className="w-12 h-12 rounded-lg object-contain"
                    />
                  )}
                  <div>
                    <h3 className="font-bold text-gray-900">{stock.name}</h3>
                    <p className="text-sm text-gray-600">
                      {stock.symbol} · {stock.exchange}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRefreshStock(stock)}
                    disabled={updating[stock.id]}
                    className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Refresh data"
                  >
                    {updating[stock.id] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleRemoveStock(stock.id, stock.symbol)}
                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove from portfolio"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Share Price</span>
                  <span className="text-lg font-bold text-gray-900">
                    {stock.current_price_formatted || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Market Cap</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {stock.market_cap_formatted || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Enterprise Value</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {stock.enterprise_value_formatted || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Shares Outstanding</span>
                  <span className="text-lg font-semibold text-gray-900">
                    {stock.shares_outstanding_formatted || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Saved {new Date(stock.saved_at).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/market-data/chart/${stock.symbol}`)}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                  >
                    <LineChart className="w-3 h-3" />
                    Chart
                  </button>
                  <button
                    onClick={() => navigate(`/market-data/news/${stock.symbol}`)}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                  >
                    <Newspaper className="w-3 h-3" />
                    News
                  </button>
                  {stock.weburl && (
                    <a
                      href={stock.weburl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                    >
                      Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;

